/**
 * Shared config + cascade helpers for permission groups.
 *
 * A group holds one permission map for one target role:
 *   { md_User: { create: true, ... }, md_Roster: { view: true, ... }, ... }
 *
 * Role scoping mirrors what the individual permission modal already does
 * (see UserPermissions.js): the General modules are PM-only, members get the
 * roster/schedule modules, and md_WorkPattern / md_Shift are role exclusive.
 */
import { permissions as generalPermissions, modulePermissionsMap } from '../../../../utils/constants';

export const GROUP_ROLES = [
    { value: 'pm', label: 'PM' },
    { value: 'member', label: 'Member' },
];

export const GENERAL_MODULES = ['md_User', 'md_Project', 'md_Task', 'md_Deviation'];

/**
 * General (create / manage / manage members / remove) modules. PMs get all four;
 * members get Tasks only, which is the one general module a member can hold.
 */
export const MEMBER_GENERAL_MODULES = ['md_Task'];

// md_Deviation always keeps create/manage/manageMembers granted (same rule as UserPermissions.handleSave)
const LOCKED_TRUE_PERMISSIONS = { md_Deviation: ['create', 'manage', 'manageMembers'] };

export const isLockedTrue = (moduleKey, permValue) =>
    (LOCKED_TRUE_PERMISSIONS[moduleKey] || []).includes(permValue);

// a member only ever works on their own tasks, which they may always manage and
// remove - the single decision is whether they may create a task at all. PM keeps
// all four, matching the individual screen.
export const MEMBER_TASK_ACTIONS = ['create'];

export const buildGeneralModules = (role) =>
    (role === 'pm' ? GENERAL_MODULES : MEMBER_GENERAL_MODULES)
        .map(key => ({
            key,
            label: key,
            permissions: role === 'pm'
                ? generalPermissions
                : generalPermissions.filter(p => MEMBER_TASK_ACTIONS.includes(p.value)),
        }));

/**
 * employee_type code -> department, labelled exactly as the user profile does:
 * the client's own roles plus the two built-in codes. Shared by the profile tab's
 * Department picker and the assigned-employee list.
 */
export const buildDepartmentOptions = (clientData, t) => [
    { value: 'no_department', label: t('No Department') },
    { value: 'z_none', label: t('Others') },
    ...((clientData && clientData.roles) || []).map(role => ({ value: role.code, label: role.name })),
];

export const buildDepartmentLabels = (clientData, t) =>
    buildDepartmentOptions(clientData, t)
        .reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {});

/** Whether a built module offers a general permission at all. */
export const offersPermission = (modules, moduleKey, permValue) =>
    ((modules.find(m => m.key === moduleKey) || {}).permissions || []).some(p => p.value === permValue);

/**
 * Roster + schedule modules for the group role, gated by the same client
 * settings used by the bulk permission grid.
 */
export const buildRosterModules = (role, flags = {}) =>
    Object.entries(modulePermissionsMap)
        .filter(([key, m]) => {
            if (role === 'pm' ? m.pmAccess === false : !m.memberAccess) return false;
            return key === 'md_Shift' ? !!flags.allowMembersToManageScheduleShift : !!flags.rosterEnabled;
        })
        .map(([key, m]) => ({
            key,
            label: m.label,
            colorClass: m.colorClass,
            permissions: m.permissions.filter(p =>
                (role === 'pm' || p.memberAccess !== false)
                && !(key === 'md_Shift' && p.value === 'editAutoTimeRecording' && !flags.allowShiftAutoTimesheetToggle)
            ),
        }));

export const buildGroupModules = (role, flags) => [...buildGeneralModules(role), ...buildRosterModules(role, flags)];

// PM general modules start with everything but `remove`
const PM_GENERAL_DEFAULTS = ['create', 'manage', 'manageMembers'];
// PM roster / schedule modules start fully granted except deleting a published roster
const PM_ROSTER_DEFAULT_OFF = { md_Roster: ['deletePublished'] };

const isGrantedByDefault = (role, moduleKey, permValue) => {
    if (isLockedTrue(moduleKey, permValue)) return true;
    // a member's single Task permission is "create"
    if (role !== 'pm') return moduleKey === 'md_Task';
    if (GENERAL_MODULES.includes(moduleKey)) return PM_GENERAL_DEFAULTS.includes(permValue);
    return !(PM_ROSTER_DEFAULT_OFF[moduleKey] || []).includes(permValue);
};

/** Starting permission map for a new group of `role`, with the default grants applied. */
export const buildEmptyPermissionMap = (role, flags) => {
    const map = {};
    buildGroupModules(role, flags).forEach(mod => {
        map[mod.key] = {};
        mod.permissions.forEach(perm => { map[mod.key][perm.value] = isGrantedByDefault(role, mod.key, perm.value); });
    });
    return map;
};

export const countGranted = (permissionMap = {}) =>
    Object.values(permissionMap).reduce((total, perms) => total + Object.values(perms || {}).filter(Boolean).length, 0);

export const countTotal = (role, flags) =>
    buildGroupModules(role, flags).reduce((total, mod) => total + mod.permissions.length, 0);

const ROSTER_SUB_MODULES = ['md_QuickPlanner', 'md_WorkPattern'];
const SHIFT_ROSTER_DEPENDENT_PERMS = ['createEditAndDelete', 'overrideAdminOrPmCreatedShift'];

/**
 * Toggles one permission and resolves every dependency the individual
 * permission screens already enforce:
 *  - intra module `requires` (both directions)
 *  - Quick Planner / Work Pattern need Manual Roster view
 *  - md_Shift create/override need Manual Roster view
 *  - md_Shift "Edit Auto Time Recording" drives "Create, Edit & Delete"
 */
export const applyPermissionChange = (permissionMap, modules, moduleKey, permValue, value) => {
    const next = {};
    Object.keys(permissionMap).forEach(key => { next[key] = { ...permissionMap[key] }; });
    if (!next[moduleKey]) next[moduleKey] = {};

    const moduleConfig = modules.find(m => m.key === moduleKey);
    next[moduleKey][permValue] = value;

    if (moduleConfig) {
        const permConfig = moduleConfig.permissions.find(p => p.value === permValue);
        if (value) {
            (permConfig?.requires || []).forEach(req => { next[moduleKey][req] = true; });
        } else {
            // disable anything in this module that depends on the permission just turned off
            const revokeDependents = () => moduleConfig.permissions.reduce((revoked, p) => {
                if (!p.requires || !next[moduleKey][p.value]) return revoked;
                if (!p.requires.some(req => !next[moduleKey][req])) return revoked;
                next[moduleKey][p.value] = false;
                return true;
            }, false);
            while (revokeDependents()) { /* keep cascading until nothing changes */ }
        }
    }

    // "Edit Auto Time Recording" grants / revokes "Create, Edit & Delete" with it
    if (moduleKey === 'md_Shift' && permValue === 'editAutoTimeRecording' && next.md_Shift) {
        next.md_Shift.createEditAndDelete = value;
        if (!value) next.md_Shift.overrideAdminOrPmCreatedShift = false;
    }

    const hasRosterModule = !!next.md_Roster;

    if (moduleKey === 'md_Roster' && permValue === 'view' && !value) {
        ROSTER_SUB_MODULES.forEach(sub => {
            if (!next[sub]) return;
            Object.keys(next[sub]).forEach(key => { next[sub][key] = false; });
        });
        if (next.md_Shift) SHIFT_ROSTER_DEPENDENT_PERMS.forEach(key => {
            if (key in next.md_Shift) next.md_Shift[key] = false;
        });
    }

    if (hasRosterModule && value) {
        const needsRosterView = ROSTER_SUB_MODULES.includes(moduleKey)
            || (moduleKey === 'md_Shift' && (SHIFT_ROSTER_DEPENDENT_PERMS.includes(permValue) || permValue === 'editAutoTimeRecording'));
        if (needsRosterView) next.md_Roster.view = true;
    }

    return next;
};

/** Grants / revokes every permission of one module, honouring the same cascade. */
export const applyModuleToggle = (permissionMap, modules, moduleKey, value) => {
    const moduleConfig = modules.find(m => m.key === moduleKey);
    if (!moduleConfig) return permissionMap;
    let next = permissionMap;
    const ordered = value
        ? moduleConfig.permissions
        : [...moduleConfig.permissions].reverse();
    ordered.forEach(perm => {
        if (isLockedTrue(moduleKey, perm.value)) return;
        next = applyPermissionChange(next, modules, moduleKey, perm.value, value);
    });
    return next;
};

/** Grants / revokes one general permission column (create, manage, ...) across all general modules. */
export const applyGeneralColumnToggle = (permissionMap, modules, permValue, value) => {
    let next = permissionMap;
    GENERAL_MODULES.forEach(moduleKey => {
        if (!next[moduleKey] || isLockedTrue(moduleKey, permValue)) return;
        if (!offersPermission(modules, moduleKey, permValue)) return;
        next = applyPermissionChange(next, modules, moduleKey, permValue, value);
    });
    return next;
};

/* ── Onboarding profile settings ──────────────────────────────
 * A group can also carry the profile fields an admin fills in when a new
 * employee joins, so putting someone in a group sets up their whole account.
 * Only fields the group explicitly manages are stored; everything else is
 * left as it is on the employee. Section order and gating follow the profile
 * form (library/schemas/user.js).
 */

export const PROFILE_SECTIONS = [
    {
        key: 'employment',
        label: 'Employment & Access',
        icon: 'fa-id-badge',
        fields: [
            // switched off for now - not offered here nor accepted by the server
            { field: 'employee_type', label: 'Department', control: 'select', options: 'departments', fallback: 'no_department', disabled: true },
            { field: 'company_shift_id', label: 'Working Shift', control: 'select', options: 'companyShifts', fallback: null, gate: 'allowCompanyShifts', required: true, requiredMessage: 'Please select a working shift' },
            { field: 'groups', label: 'Groups', control: 'groups', fallback: '' },
            { field: 'user_skills', label: 'Skills', control: 'skills', fallback: '', gate: 'allowResourcePlanner' },
            { field: 'leave_type_grants', label: 'Leave Type', control: 'multiselect', options: 'leaveTypes', fallback: '' },
            { field: 'allow_mobile_app_access', label: 'Allow mobile access', control: 'boolean', fallback: false, gate: 'mobileAccessBlocked' },
            { field: 'vacation_days', label: 'Vacation Days', control: 'number', fallback: 0, min: 0 },
            { field: 'employee_percent', label: 'Employee percent', control: 'number', fallback: 100, min: 1, max: 100, rangeMessage: 'Employment percentage should be between 1 to 100' },
            { field: 'work_time_agreement', label: 'Work time agreement (Without Break)', control: 'number', fallback: 37.5, min: 0, step: 0.25, multipleOf: 0.25 },
        ],
    },
    {
        key: 'time',
        label: 'Time & Attendance',
        icon: 'fa-clock-o',
        fields: [
            { field: 'allow_user_for_auto_timesheet', label: 'Auto time recording', control: 'boolean', fallback: false, gate: 'allowUserAutoTimesheetToggle' },
            { field: 'auto_break', label: 'Auto break', control: 'boolean', fallback: true },
            { field: 'exclude_break_in_working_hours_rule', label: 'Shift code excludes break', control: 'boolean', fallback: false, gate: 'allowAutomaticFlexitime' },
            { field: 'follow_schedule_for_absence', label: 'Follow schedule shift hours while taking absence', control: 'boolean', fallback: false },
            { field: 'allow_auto_flexitime', label: 'Allow automatic flexitime', control: 'boolean', fallback: false, gate: 'allowAutomaticFlexitime' },
            { field: 'max_negative_hours_flexitime', label: 'Maximum negative flexitime hours', control: 'number', fallback: 0, max: 0, rangeMessage: 'Negative Flexitime can only be in Negative', gate: 'allowAutomaticFlexitime' },
            { field: 'max_positive_hours_flexitime', label: 'Maximum positive flexitime hours', control: 'number', fallback: 0, min: 0, rangeMessage: 'Positive Flexitime can only be in Positive', gate: 'allowAutomaticFlexitime' },
        ],
    },
    {
        key: 'overtime',
        label: 'Overtime rules',
        icon: 'fa-hourglass-half',
        // appended as a dated mapping rather than overwritten - see the note in
        // PermissionGroupProfile
        note: 'Pick the rule and the period it applies for. Employees already on an active rule keep it.',
        // overtime rules follow the working shift, so they are only offered once the
        // group manages the Working Shift field
        requiresField: 'company_shift_id',
        fields: [
            {
                field: 'weekly_overtime_id',
                label: 'Weekly Overtime Settings',
                control: 'overtime_rule',
                options: 'weeklyOvertimeRules',
                fallback: null,
                gate: 'hasWeeklyOvertimeRules',
                fromField: 'weekly_overtime_valid_from',
                toField: 'weekly_overtime_valid_to',
                // weekly rules run Monday-Sunday, the same constraint the per-user
                // endpoint enforces
                mondayAligned: true,
            },
            {
                field: 'overtime_cap_setting_id',
                label: 'Overtime Cap Settings',
                control: 'overtime_rule',
                options: 'overtimeCapRules',
                fallback: null,
                gate: 'hasOvertimeCapRules',
                fromField: 'overtime_cap_valid_from',
                toField: 'overtime_cap_valid_to',
                mondayAligned: false,
            },
        ],
    },
    {
        key: 'payment',
        label: 'Payment settings',
        icon: 'fa-money',
        fields: [
            { field: 'payment_mode', label: 'Remuneration type', control: 'select', options: 'paymentModes', fallback: 'hourly' },
            { field: 'hourly_rate', label: 'Hourly/Fixed Rate', control: 'number', fallback: 0, min: 0 },
            { field: 'payment_cycle', label: 'Payment period', control: 'select', options: 'paymentCycles', fallback: 'monthly' },
            { field: 'expected_fixed_hours', label: 'Estimated hours', control: 'number', fallback: 0, min: 0 },
            { field: 'weekend_extra_payment_percent', label: 'Weekend extra percent (%)', control: 'number', fallback: 0, min: 0 },
            // switched off for now, like Department
            { field: 'extra_payments', label: 'Additional Payments', control: 'multiselect', options: 'extraPayments', fallback: '', disabled: true },
            { field: 'paid_overtime_renewal_hours', label: 'Paid Overtime Renewal (hours)', control: 'number', fallback: 0, min: 0, gate: 'paidOvertimeRenewalConfigured' },
        ],
    },
];

export const isProfileFieldManaged = (profileSettings = {}, field) => field in (profileSettings || {});

/**
 * Sections with only the fields this client's settings actually allow. A section
 * that depends on another field (`requiresField`) is dropped until the group
 * manages that field.
 */
export const buildProfileSections = (flags = {}, profileSettings = {}) => PROFILE_SECTIONS
    .filter(section => !section.requiresField || isProfileFieldManaged(profileSettings, section.requiresField))
    .map(section => ({ ...section, fields: section.fields.filter(item => !item.disabled && (!item.gate || flags[item.gate])) }))
    .filter(section => section.fields.length);

export const getProfileFieldMap = (flags, profileSettings) => buildProfileSections(flags, profileSettings)
    .reduce((acc, section) => {
        section.fields.forEach(item => { acc[item.field] = item; });
        return acc;
    }, {});

export const countProfileFields = (flags, profileSettings) => buildProfileSections(flags, profileSettings)
    .reduce((total, section) => total + section.fields.length, 0);

export const countManagedProfileFields = (profileSettings = {}, flags) => {
    const allowed = getProfileFieldMap(flags, profileSettings);
    return Object.keys(profileSettings).filter(field => allowed[field]).length;
};

/** Fields of the sections hidden behind `field`, dropped when it stops being managed. */
const getDependentFields = (field) => PROFILE_SECTIONS
    .filter(section => section.requiresField === field)
    .flatMap(section => section.fields)
    .flatMap(item => [item.field, item.fromField, item.toField].filter(Boolean));

/**
 * Starts managing a field (seeding its fallback) or stops managing it entirely.
 * The overtime rule fields carry their own validity dates, so those move with them.
 */
export const setProfileFieldManaged = (profileSettings = {}, item, managed) => {
    const next = { ...profileSettings };
    if (managed) {
        next[item.field] = item.fallback;
        if (item.fromField) next[item.fromField] = null;
        if (item.toField) next[item.toField] = null;
    } else {
        delete next[item.field];
        if (item.fromField) delete next[item.fromField];
        if (item.toField) delete next[item.toField];
        getDependentFields(item.field).forEach(field => { delete next[field]; });
    }
    return next;
};

// the user profile caps estimated hours by payment period (UsersForm.validateUser)
const MAX_EXPECTED_HOURS = {
    monthly: { limit: 743, message: 'Max monthly overtime hours limit is 743 hours' },
    weekly: { limit: 167, message: 'Max weekly overtime hours limit is 167 hours' },
};

const isEmpty = value => value === null || value === undefined || value === '';

// float-safe "is a multiple of" (0.1 + 0.2 style rounding must not fail 37.5)
const isMultipleOf = (value, step) => Math.abs(value / step - Math.round(value / step)) < 1e-9;

const getNumberErrors = (item, value, profileSettings) => {
    if (isEmpty(value) || !Number.isFinite(Number(value))) return ['is a required property'];
    const number = Number(value);
    const errors = [];
    if (item.min !== undefined && number < item.min) errors.push(item.rangeMessage || `should be >= ${item.min}`);
    if (item.max !== undefined && number > item.max) errors.push(item.rangeMessage || `should be <= ${item.max}`);
    if (item.multipleOf && !isMultipleOf(number, item.multipleOf)) errors.push(`should be multiple of ${item.multipleOf}`);
    if (item.field === 'expected_fixed_hours') {
        // without a managed payment period the employee keeps their own, so only
        // the looser monthly cap can be checked here
        const cycle = MAX_EXPECTED_HOURS[profileSettings.payment_cycle] || MAX_EXPECTED_HOURS.monthly;
        if (number > cycle.limit) errors.push(cycle.message);
    }
    return [...new Set(errors)];
};

/**
 * Validation errors for every managed field, keyed by field (overtime validity
 * dates are keyed by their own field). Messages are untranslated keys, shown
 * under the control the same way the user profile form shows them.
 */
export const getProfileFieldErrors = (profileSettings = {}, flags) => {
    const errors = {};
    const add = (field, messages) => { if (messages.length) errors[field] = [...(errors[field] || []), ...messages]; };

    buildProfileSections(flags, profileSettings)
        .flatMap(section => section.fields)
        .filter(item => isProfileFieldManaged(profileSettings, item.field))
        .forEach(item => {
            const value = profileSettings[item.field];
            if (item.control === 'overtime_rule') {
                if (isEmpty(value)) add(item.field, ['is a required property']);
                if (isEmpty(profileSettings[item.fromField])) add(item.fromField, ['Please select a valid from date']);
                return;
            }
            if (item.required && isEmpty(value)) return add(item.field, [item.requiredMessage || 'is a required property']);
            if (item.control === 'number') add(item.field, getNumberErrors(item, value, profileSettings));
        });
    return errors;
};

/** Whether any field of a section currently fails validation. */
export const sectionHasErrors = (section, errors = {}) =>
    section.fields.some(item => [item.field, item.fromField].some(field => field && errors[field]));

export const setProfileFieldValue = (profileSettings = {}, field, value) => ({ ...profileSettings, [field]: value });

export const getSectionManagedState = (profileSettings = {}, section) => {
    const managed = section.fields.filter(item => isProfileFieldManaged(profileSettings, item.field)).length;
    if (!managed) return 'none';
    return managed === section.fields.length ? 'all' : 'partial';
};

export const setSectionManaged = (profileSettings = {}, section, managed) => {
    let next = { ...profileSettings };
    section.fields.forEach(item => { next = setProfileFieldManaged(next, item, managed); });
    return next;
};

export const getModuleState = (permissionMap, moduleConfig) => {
    const perms = permissionMap?.[moduleConfig.key] || {};
    const granted = moduleConfig.permissions.filter(p => perms[p.value]).length;
    if (!granted) return 'none';
    return granted === moduleConfig.permissions.length ? 'all' : 'partial';
};

export const getGeneralColumnState = (permissionMap, generalModules, permValue) => {
    const keys = GENERAL_MODULES.filter(key =>
        permissionMap?.[key] && offersPermission(generalModules, key, permValue));
    if (!keys.length) return 'none';
    const granted = keys.filter(key => permissionMap[key][permValue]).length;
    if (!granted) return 'none';
    return granted === keys.length ? 'all' : 'partial';
};
